<script setup>
import { ref, computed, onMounted } from 'vue'
import PanelOrganic from '@/design/primitives/PanelOrganic.vue'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import FieldLeaf from '@/design/primitives/FieldLeaf.vue'
import { api } from '@/api/client.js'
import { EP } from '@/api/endpoints.js'

const props = defineProps({
  activityTokens: { type: [Number, String], default: 0 },
  user: { type: Object, default: null },
})

async function openInvoice(w) {
  if (!w?.withdrawalId) return
  try {
    const res = await api.get(
      `/api/user/withdrawals/${encodeURIComponent(w.withdrawalId)}/invoice/pdf`,
      { responseType: 'blob' }
    )
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `invoice-${w.withdrawalId}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 5000)
  } catch (err) {
    console.error('Failed to download invoice:', err)
  }
}

const withdrawals = ref([])
const loading     = ref(false)
const error       = ref(null)
let   fetched     = false

const showForm    = ref(false)
const formAmount  = ref('')
const submitting  = ref(false)
const submitError = ref(null)

const TOKENS_PER_EUR = 100
const MIN_EUR = 5
const MIN_TOKENS = Math.ceil(MIN_EUR * TOKENS_PER_EUR)

// Per-method availability (SEPA / card-OCT) — admin-controlled via /api/withdrawals/config.
const methods = ref({ sepa: true, card: true })
const selectedMethod = ref('sepa')
const anyMethod = computed(() => methods.value.sepa || methods.value.card)
const bothMethods = computed(() => methods.value.sepa && methods.value.card)

async function loadConfig() {
  try {
    const res = await api.get('/api/withdrawals/config')
    methods.value = { sepa: !!res.data?.sepaEnabled, card: !!res.data?.cardEnabled }
    selectedMethod.value = methods.value.sepa ? 'sepa' : (methods.value.card ? 'card' : '')
  } catch { /* keep defaults */ }
}

async function load() {
  if (fetched) return
  loading.value = true
  error.value   = null
  try {
    const res = await api.get(EP.userWithdrawals)
    withdrawals.value = Array.isArray(res.data) ? res.data : []
    fetched = true
  } catch (e) {
    error.value = e.message || 'Could not load withdrawals.'
  } finally {
    loading.value = false
  }
}

async function submitWithdrawal() {
  submitError.value = null
  const amount = parseFloat(formAmount.value)
  if (!amount || amount <= 0) {
    submitError.value = 'Enter a valid amount.'
    return
  }
  const available = Number(props.activityTokens) || 0
  if (amount > available) {
    submitError.value = 'Insufficient Activity Tokens.'
    return
  }
  if (amount < MIN_TOKENS) {
    submitError.value = `Minimum withdrawal is ${MIN_EUR} EUR (${MIN_TOKENS} AT).`
    return
  }
  if (!anyMethod.value || !selectedMethod.value) {
    submitError.value = 'No payout method is currently available.'
    return
  }
  submitting.value = true
  try {
    // Server initiates GATEWAY hosted form and returns redirect-url for the
    // gateway page where the player enters their destination card details.
    const res = await api.post(EP.userWithdraw, { tokenAmount: amount, method: selectedMethod.value })
    if (res.data?.redirectUrl) {
      window.location.href = res.data.redirectUrl
      return
    }
    submitError.value = 'Gateway did not return a redirect URL.'
  } catch (e) {
    submitError.value = e.response?.data?.message || e.message || 'Withdrawal failed.'
  } finally {
    submitting.value = false
  }
}

function formatDate(raw) {
  if (!raw) return '—'
  return new Date(raw).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  })
}

function toggleForm() {
  showForm.value = !showForm.value
  if (!showForm.value) {
    submitError.value = null
    formAmount.value  = ''
  }
}

onMounted(() => { load(); loadConfig() })
</script>

<template>
  <section class="dossier-section" aria-labelledby="withdrawals-heading">
    <div class="dossier-section__header">
      <div class="dossier-section__title">
        <h2 id="withdrawals-heading" class="stamp u-display u-allcaps">Activity Tokens</h2>
        <span class="at-balance u-mono">
          {{ Number(activityTokens || 0).toLocaleString() }} AT
        </span>
      </div>
      <ButtonSeed variant="primary" size="sm" @click="toggleForm">
        {{ showForm ? 'CLOSE' : 'WITHDRAW' }}
      </ButtonSeed>
    </div>

    <p class="at-desc">
      Activity Tokens are a special in-game currency earned exclusively through
      completing quests, daily and seasonal activities, reporting bugs, and engaging
      with core gameplay systems. They represent active participation and
      progression within the game world.
    </p>

    <div class="at-rate-row">
      <div class="at-rate-pill">
        <span class="at-rate-label u-display u-allcaps">Rate</span>
        <span class="u-mono">{{ TOKENS_PER_EUR }} AT = 1 EUR</span>
      </div>
      <div class="at-rate-pill">
        <span class="at-rate-label u-display u-allcaps">Min</span>
        <span class="u-mono">{{ MIN_EUR }} EUR ({{ MIN_TOKENS }} AT)</span>
      </div>
    </div>

    <!-- Inline withdrawal form -->
    <Transition name="dossier-form">
      <PanelOrganic v-if="showForm" tone="strong" :padded="true" class="withdraw-form">
        <p class="withdraw-form__intro">
          Enter the amount of Activity Tokens to withdraw. You'll be redirected
          to the payment gateway where you enter the destination details —
          we never see or store them.
        </p>

        <!-- Payout method — only the methods enabled by the admin are offered -->
        <div v-if="!anyMethod" class="withdraw-form__method-note">
          No payout method is currently available. Please check back later.
        </div>
        <div v-else-if="bothMethods" class="payout-tabs" role="tablist" aria-label="Payout method">
          <button
            type="button" class="payout-tab"
            :class="{ 'payout-tab--active': selectedMethod === 'sepa' }"
            @click="selectedMethod = 'sepa'"
          >SEPA</button>
          <button
            type="button" class="payout-tab"
            :class="{ 'payout-tab--active': selectedMethod === 'card' }"
            @click="selectedMethod = 'card'"
          >CARD (OCT)</button>
        </div>
        <p v-else class="withdraw-form__method-note">
          Payout method: <strong>{{ selectedMethod === 'sepa' ? 'SEPA bank transfer' : 'Card (OCT)' }}</strong>
        </p>

        <div class="withdraw-form__row">
          <FieldLeaf
            v-model="formAmount"
            label="AMOUNT (ACTIVITY TOKENS)"
            type="number"
            placeholder="0"
            :mono="true"
          />
          <div class="withdraw-form__submit">
            <ButtonSeed
              variant="primary"
              size="md"
              :disabled="submitting || !anyMethod"
              @click="submitWithdrawal"
            >
              {{ submitting ? 'REDIRECTING…' : 'CONTINUE' }}
            </ButtonSeed>
          </div>
        </div>
        <p v-if="submitError" class="withdraw-form__err" role="alert">{{ submitError }}</p>
      </PanelOrganic>
    </Transition>

    <PanelOrganic tone="strong" :padded="true">
      <!-- Loading -->
      <div v-if="loading" class="ledger-state" aria-live="polite">
        <span class="dossier-spinner" aria-hidden="true" />
        <span class="ledger-state__text u-display u-allcaps">Loading withdrawals…</span>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="ledger-state ledger-state--err" aria-live="assertive">
        <span class="ledger-state__text">{{ error }}</span>
        <ButtonSeed variant="ghost" size="sm" @click="() => { fetched = false; load() }">RETRY</ButtonSeed>
      </div>

      <!-- Empty -->
      <div v-else-if="!withdrawals.length" class="ledger-state ledger-state--empty">
        <span class="u-display u-allcaps">No withdrawals yet</span>
      </div>

      <!-- Ledger -->
      <ul v-else class="ledger" role="list" aria-label="Withdrawals">
        <li class="ledger__header" aria-hidden="true">
          <span class="ledger__col ledger__col--id   u-display u-allcaps">ID</span>
          <span class="ledger__col ledger__col--date u-display u-allcaps">Date</span>
          <span class="ledger__col ledger__col--amount u-display u-allcaps">Amount</span>
          <span class="ledger__col ledger__col--status u-display u-allcaps">Status</span>
        </li>

        <li
          v-for="w in withdrawals"
          :key="w.withdrawalId ?? w.id"
          class="ledger__row"
        >
          <span class="ledger__col ledger__col--id u-mono ledger__id">{{ w.withdrawalId ?? '—' }}</span>
          <span class="ledger__col ledger__col--date u-mono">{{ formatDate(w.createdAt) }}</span>
          <div class="ledger__col ledger__col--amount">
            <span class="u-mono at-amount">
              {{ parseFloat(Number(w.token_amount ?? w.amount ?? 0).toFixed(2)) }} AT
            </span>
          </div>
          <div class="ledger__col ledger__col--status">
            <span
              class="ledger__badge"
              :class="`badge--${(w.status || 'pending').toLowerCase()}`"
            >
              {{ w.status || 'pending' }}
            </span>
            <button
              v-if="w.status === 'approved'"
              class="ledger__invoice-btn"
              title="Open payout invoice"
              @click="openInvoice(w)"
            >
              <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
                <rect x="3" y="2" width="10" height="12" rx="1"/>
                <line x1="5.5" y1="5.5" x2="10.5" y2="5.5"/>
                <line x1="5.5" y1="8" x2="10.5" y2="8"/>
                <line x1="5.5" y1="10.5" x2="9" y2="10.5"/>
              </svg>
            </button>
          </div>
        </li>
      </ul>
    </PanelOrganic>
  </section>
</template>

<style scoped>
.dossier-section {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.dossier-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-4);
  flex-wrap: wrap;
}

.dossier-section__title {
  display: flex;
  align-items: baseline;
  gap: var(--sp-3);
  flex-wrap: wrap;
}

.stamp {
  font-size: clamp(var(--fs-21), 2.5vw, var(--fs-32));
  color: var(--gold);
  letter-spacing: 0.12em;
  margin: 0;
}

.at-balance {
  font-size: var(--fs-18);
  color: var(--gold);
  letter-spacing: 0.02em;
}

.at-desc {
  margin: 0;
  font-size: var(--fs-13);
  line-height: 1.65;
  color: var(--ink-mute);
  padding: var(--sp-3) var(--sp-4);
  background: var(--gold-soft);
  border-left: 3px solid var(--gold);
  border-radius: var(--r-xs);
}

.at-rate-row {
  display: flex;
  gap: var(--sp-3);
  flex-wrap: wrap;
}
.at-rate-pill {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid rgba(229, 199, 107, 0.28);
  border-radius: var(--r-xs);
  background: var(--gold-soft);
  font-size: var(--fs-13);
  color: var(--ink-soft);
}
.at-rate-label {
  font-size: var(--fs-11, 0.6875rem);
  color: var(--ink-mute);
  letter-spacing: 0.14em;
}

.at-amount {
  color: var(--gold);
}

/* Withdrawal form */
.withdraw-form {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

.withdraw-form__intro {
  margin: 0;
  font-size: var(--fs-14);
  color: var(--ink-soft);
}

.withdraw-form__method-note {
  margin: 0;
  font-size: var(--fs-13);
  color: var(--ink-soft);
}
.withdraw-form__method-note strong { color: var(--gold); }

.withdraw-form__row {
  display: flex;
  align-items: flex-end;
  gap: var(--sp-4);
  flex-wrap: wrap;
}

.withdraw-form__row > :first-child { flex: 1; min-width: 160px; }

.withdraw-form__submit { padding-bottom: 0; }
/* Lock both FieldLeaf input and REQUEST/SAVE buttons to one explicit height so
   browser line-height differences can't make them disagree by a few px. */
.withdraw-form :deep(.field__input) {
  height: 50px;
  padding-top: 0;
  padding-bottom: 0;
  box-sizing: border-box;
}
.withdraw-form__submit :deep(.btn),
.payout-edit__actions :deep(.btn) {
  height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.withdraw-form__err {
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--fs-12);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ember);
}

/* Payout details block inside the form panel */
.payout-block {
  border: 1px solid var(--ash);
  border-radius: var(--r-sm);
  padding: var(--sp-3) var(--sp-4);
  background: rgba(11, 10, 8, 0.35);
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}
.payout-block__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.payout-block__title {
  font-size: var(--fs-11, 0.6875rem);
  color: var(--ink-mute);
  letter-spacing: 0.16em;
}
.payout-block__edit {
  background: none;
  border: 1px solid var(--ash);
  color: var(--ink-soft);
  font-family: var(--font-display);
  font-size: var(--fs-11, 0.6875rem);
  letter-spacing: 0.12em;
  padding: 6px 12px;
  border-radius: var(--r-xs);
  cursor: pointer;
  transition: color var(--t-fast) var(--ease-cinema), border-color var(--t-fast) var(--ease-cinema);
}
.payout-block__edit:hover { color: var(--gold); border-color: var(--gold); }
.payout-block__edit:disabled { opacity: 0.4; cursor: not-allowed; }
.payout-block__summary {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  font-size: var(--fs-14);
  color: var(--ink-soft);
}
.payout-block__method {
  display: inline-block;
  padding: 2px 10px;
  border: 1px solid var(--gold);
  border-radius: var(--r-xs);
  font-size: var(--fs-11, 0.6875rem);
  color: var(--gold);
  letter-spacing: 0.14em;
}
.payout-edit { display: flex; flex-direction: column; gap: var(--sp-3); }
.payout-tabs {
  display: inline-flex;
  border: 1px solid var(--ash);
  border-radius: var(--r-xs);
  overflow: hidden;
  align-self: flex-start;
}
.payout-tab {
  background: transparent;
  border: none;
  padding: 6px 14px;
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.14em;
  color: var(--ink-mute);
  cursor: pointer;
  transition: background var(--t-fast) var(--ease-cinema), color var(--t-fast) var(--ease-cinema);
}
.payout-tab:hover { color: var(--ink); }
.payout-tab--active { background: var(--gold-soft); color: var(--gold); }
.payout-edit__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
}
.payout-edit__actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--sp-3);
}
.payout-edit__actions :deep(.btn) {
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
@media (max-width: 580px) {
  .payout-edit__grid { grid-template-columns: 1fr; }
}

/* OK notice */
.dossier-ok {
  font-size: var(--fs-12);
  letter-spacing: 0.14em;
  color: var(--gold);
  background: var(--gold-soft);
  padding: var(--sp-3) var(--sp-4);
  border-radius: var(--r-sm);
  border: 1px solid rgba(229, 199, 107, 0.3);
  margin: 0;
}

/* Ledger */
.ledger {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.ledger__header,
.ledger__row {
  display: grid;
  grid-template-columns: 1fr 100px 130px 110px;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-3) var(--sp-4);
}

.ledger__header {
  border-bottom: 1px solid var(--ash);
  margin-bottom: var(--sp-1);
}

.ledger__header .ledger__col {
  font-size: var(--fs-11, 0.6875rem);
  color: var(--ink-mute);
  letter-spacing: 0.14em;
}

.ledger__row {
  border-bottom: 1px solid var(--ash);
  transition: background var(--t-fast) var(--ease-cinema);
}
.ledger__row:last-child { border-bottom: none; }
.ledger__row:hover { background: rgba(229, 199, 107, 0.04); }

.ledger__col { min-width: 0; font-size: var(--fs-14); color: var(--ink-soft); }

.ledger__id {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--fs-12);
  color: var(--ink-mute);
}

.ledger__col--amount {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ledger__col--status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.ledger__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4px 10px;
  border-radius: var(--r-xs);
  font-family: var(--font-display);
  font-size: var(--fs-11, 0.6875rem);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  white-space: nowrap;
  border: 1px solid var(--ash);
  background: var(--bg-deep);
  color: var(--ink-mute);
}
.ledger__invoice-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid rgba(229, 199, 107, 0.3);
  border-radius: var(--r-xs);
  background: transparent;
  color: var(--gold);
  cursor: pointer;
  transition: color var(--t-fast) var(--ease-cinema), border-color var(--t-fast) var(--ease-cinema), background var(--t-fast) var(--ease-cinema);
}
.ledger__invoice-btn:hover {
  color: #F5DC97;
  border-color: rgba(229, 199, 107, 0.6);
  background: var(--gold-soft);
}
.badge--completed,
.badge--paid      { background: var(--gold-soft); color: var(--gold); border-color: rgba(229, 199, 107, 0.3); }
.badge--pending   { background: rgba(184, 90, 58, 0.12); color: var(--copper); border-color: rgba(184, 90, 58, 0.3); }
.badge--failed,
.badge--rejected  { background: rgba(230, 76, 43, 0.12); color: var(--ember); border-color: rgba(230, 76, 43, 0.3); }

/* States */
.ledger-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-3);
  padding: var(--sp-12) var(--sp-4);
  color: var(--ink-soft);
}
.ledger-state--err   { color: var(--ember); flex-direction: column; }
.ledger-state--empty { color: var(--ink-mute); }
.ledger-state__text  { font-size: var(--fs-14); }

.dossier-spinner {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 2px solid var(--ash);
  border-top-color: var(--gold);
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
  flex-shrink: 0;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Form transition */
.dossier-form-enter-active,
.dossier-form-leave-active {
  transition: opacity var(--t-base) var(--ease-cinema),
              transform var(--t-base) var(--ease-cinema);
}
.dossier-form-enter-from,
.dossier-form-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 580px) {
  .ledger__header,
  .ledger__row {
    grid-template-columns: 1fr 100px 90px;
  }
  .ledger__col--date { display: none; }
}
</style>
